import { useState } from 'react';
import './GoalsSection.css';

const CATEGORY_COLORS = {
  'Short-term':  '#0891b2',
  'Medium-term': '#d97706',
  'Long-term':   '#7c3aed',
  'Legacy':      '#b45309',
};

function getCategoryColor(category) {
  return CATEGORY_COLORS[category] || 'var(--color-primary, #004358)';
}

function getMilestoneLabel(selectedCount, completeCount) {
  if (selectedCount === 0) return null;
  if (completeCount === selectedCount) return { emoji: '🏆', text: 'All goals detailed!' };
  if (selectedCount >= 3) return { emoji: '⭐', text: 'Great coverage!' };
  if (selectedCount === 1) return { emoji: '🎯', text: 'First goal added!' };
  return { emoji: '🎯', text: `${selectedCount} goals selected` };
}

export default function GoalsSection({ goalTemplates, profileData, onSave, saving }) {
  const existing = profileData?.selected_goals || [];

  const [stage, setStage] = useState('select'); // 'select' | 'fill'

  const [selectedGoals, setSelectedGoals] = useState(() => {
    const map = {};
    for (let i = 0; i < existing.length; i++) {
      map[existing[i].goal_template_uid] = { ...(existing[i].field_responses || {}) };
    }
    return map;
  });

  const [collapsedGoals, setCollapsedGoals] = useState(new Set());

  function isSelected(templateUid) {
    return Object.prototype.hasOwnProperty.call(selectedGoals, templateUid);
  }

  function handleToggleGoal(template) {
    if (isSelected(template.uid)) {
      setSelectedGoals(prev => {
        const next = { ...prev };
        delete next[template.uid];
        return next;
      });
      setCollapsedGoals(prev => {
        const next = new Set(prev);
        next.delete(template.uid);
        return next;
      });
    } else {
      setSelectedGoals(prev => ({ ...prev, [template.uid]: {} }));
    }
  }

  function handleFieldChange(templateUid, fieldUid, value) {
    setSelectedGoals(prev => ({
      ...prev,
      [templateUid]: { ...(prev[templateUid] || {}), [fieldUid]: value }
    }));
  }

  function toggleCollapse(templateUid) {
    setCollapsedGoals(prev => {
      const next = new Set(prev);
      if (next.has(templateUid)) next.delete(templateUid);
      else next.add(templateUid);
      return next;
    });
  }

  function isGoalComplete(template) {
    const responses = selectedGoals[template.uid] || {};
    const hasRequired = template.fields.some(f => f.required);
    if (!hasRequired) return false;
    for (let i = 0; i < template.fields.length; i++) {
      const field = template.fields[i];
      if (!field.required) continue;
      const val = responses[field.uid];
      if (!val || val.toString().trim() === '') return false;
    }
    return true;
  }

  function getFieldFillCount(template) {
    const responses = selectedGoals[template.uid] || {};
    let filled = 0;
    for (let i = 0; i < template.fields.length; i++) {
      const val = responses[template.fields[i].uid];
      if (val && val.toString().trim() !== '') filled++;
    }
    return filled;
  }

  function handleSave() {
    const payload = [];
    const keys = Object.keys(selectedGoals);
    for (let i = 0; i < keys.length; i++) {
      payload.push({ goalTemplateUid: keys[i], fieldResponses: selectedGoals[keys[i]] });
    }
    onSave(payload);
  }

  // Group templates by category
  const groups = {};
  const groupOrder = [];
  for (let i = 0; i < goalTemplates.length; i++) {
    const t = goalTemplates[i];
    const cat = t.category || 'Other';
    if (!groups[cat]) {
      groups[cat] = [];
      groupOrder.push(cat);
    }
    groups[cat].push(t);
  }

  const selectedTemplates = goalTemplates.filter(t => isSelected(t.uid));
  const completeCount = selectedTemplates.filter(t => isGoalComplete(t)).length;
  const milestone = getMilestoneLabel(selectedTemplates.length, completeCount);
  const selectedCount = selectedTemplates.length;

  /* ── Stage 1: Goal Selection ───────────────────────────────── */
  if (stage === 'select') {
    return (
      <div className="goals-section">
        <div className="goals-section__stage-header">
          <div className="goals-section__stage-title">Choose your client's goals</div>
          <div className="goals-section__stage-sub">Select all that apply — you can always come back to add or remove</div>
        </div>

        <div className="goals-section__palette-scroll">
          {groupOrder.map(cat => (
            <div key={cat} className="goals-section__cat-group">
              <div
                className="goals-section__cat-header"
                style={{ '--cat-color': getCategoryColor(cat) }}
              >
                <span className="goals-section__cat-dot" />
                {cat}
              </div>
              <div className="goals-section__cat-row">
                {groups[cat].map(template => {
                  const sel = isSelected(template.uid);
                  return (
                    <button
                      key={template.uid}
                      className={`goals-section__tile ${sel ? 'goals-section__tile--selected' : ''}`}
                      onClick={() => handleToggleGoal(template)}
                      title={sel ? 'Click to deselect' : (template.description || template.goal_name)}
                    >
                      <span className="goals-section__tile-icon">{template.icon || '🎯'}</span>
                      <span className="goals-section__tile-name">{template.goal_name}</span>
                      {sel && (
                        <>
                          <span className="goals-section__tile-check goals-section__tile-check--normal">✓</span>
                          <span className="goals-section__tile-check goals-section__tile-check--hover">×</span>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="goals-section__footer">
          <span className="goals-section__count">
            {selectedCount === 0
              ? 'No goals selected yet'
              : `${selectedCount} goal${selectedCount !== 1 ? 's' : ''} selected`}
          </span>
          <button
            className="btn--primary"
            onClick={() => setStage('fill')}
            disabled={selectedCount === 0}
          >
            Continue to fill in goals →
          </button>
        </div>
      </div>
    );
  }

  /* ── Stage 2: Form Filling ─────────────────────────────────── */
  return (
    <div className="goals-section">
      <div className="goals-section__fill-header">
        <div className="goals-section__fill-header-left">
          <button
            className="goals-section__back-btn"
            onClick={() => setStage('select')}
          >
            ← Change goals
          </button>
          <div>
            <div className="goals-section__stage-title">Fill in goal details</div>
            <div className="goals-section__stage-sub">{selectedCount} goal{selectedCount !== 1 ? 's' : ''} selected · {completeCount} complete</div>
          </div>
        </div>
        <div className="goals-section__fill-progress-track">
          <div
            className={`goals-section__fill-progress-fill ${completeCount === selectedCount ? 'goals-section__fill-progress-fill--done' : ''}`}
            style={{ width: `${selectedCount > 0 ? (completeCount / selectedCount) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="goals-section__grid-scroll">
        {milestone && (
          <div className={`goals-section__milestone-banner ${completeCount === selectedCount ? 'goals-section__milestone-banner--done' : ''}`}>
            {milestone.emoji} {milestone.text}
          </div>
        )}

        <div className="goals-section__grid">
          {selectedTemplates.map(template => {
            const responses = selectedGoals[template.uid] || {};
            const complete = isGoalComplete(template);
            const collapsed = collapsedGoals.has(template.uid);
            const filled = getFieldFillCount(template);
            const total = template.fields.length;
            const catColor = getCategoryColor(template.category);

            return (
              <div
                key={template.uid}
                className={`goals-section__card ${complete ? 'goals-section__card--complete' : ''} ${collapsed ? 'goals-section__card--collapsed' : ''}`}
                style={{ '--card-accent': catColor }}
              >
                {/* Card header */}
                <div className="goals-section__card-header">
                  <span className="goals-section__card-icon">{template.icon || '🎯'}</span>
                  <div className="goals-section__card-meta">
                    <span className="goals-section__card-name">{template.goal_name}</span>
                    <span className="goals-section__card-cat" style={{ color: catColor }}>{template.category}</span>
                  </div>
                  {complete && !collapsed && (
                    <span className="goals-section__complete-badge">✓</span>
                  )}
                  <button
                    className="goals-section__collapse-btn"
                    onClick={() => toggleCollapse(template.uid)}
                    title={collapsed ? 'Expand' : 'Minimise'}
                  >
                    {collapsed ? '▸' : '▾'}
                  </button>
                </div>

                {/* Field progress bar */}
                {total > 0 && (
                  <div className="goals-section__card-progress">
                    <div className="goals-section__card-progress-track">
                      <div
                        className="goals-section__card-progress-fill"
                        style={{
                          width: `${(filled / total) * 100}%`,
                          backgroundColor: complete ? '#16a34a' : catColor
                        }}
                      />
                    </div>
                    <span className="goals-section__card-progress-label">{filled}/{total}</span>
                  </div>
                )}

                {/* Collapsible body */}
                {!collapsed && (
                  <div className="goals-section__card-body">
                    {template.description && (
                      <p className="goals-section__card-desc">{template.description}</p>
                    )}
                    <div className="goals-section__card-fields">
                      {template.fields.map(field => (
                        <div key={field.uid} className="goals-section__card-field">
                          <label className="goals-section__field-label">
                            {field.label}
                            {field.required && <span className="goals-section__required">*</span>}
                          </label>
                          {(field.field_type === 'text' || field.field_type === 'phone' || field.field_type === 'email') && (
                            <input
                              className="goals-section__input"
                              type="text"
                              value={responses[field.uid] || ''}
                              onChange={e => handleFieldChange(template.uid, field.uid, e.target.value)}
                              placeholder={field.hint || ''}
                            />
                          )}
                          {field.field_type === 'number' && (
                            <input
                              className="goals-section__input"
                              type="number"
                              value={responses[field.uid] || ''}
                              onChange={e => handleFieldChange(template.uid, field.uid, e.target.value)}
                              placeholder={field.hint || ''}
                            />
                          )}
                          {field.field_type === 'date' && (
                            <input
                              className="goals-section__input"
                              type="date"
                              value={responses[field.uid] || ''}
                              onChange={e => handleFieldChange(template.uid, field.uid, e.target.value)}
                            />
                          )}
                          {field.field_type === 'dropdown' && (
                            <select
                              className="goals-section__select"
                              value={responses[field.uid] || ''}
                              onChange={e => handleFieldChange(template.uid, field.uid, e.target.value)}
                            >
                              <option value="">Select...</option>
                              {(field.options || []).map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          )}
                          {field.field_type === 'textarea' && (
                            <textarea
                              className="goals-section__textarea"
                              value={responses[field.uid] || ''}
                              onChange={e => handleFieldChange(template.uid, field.uid, e.target.value)}
                              placeholder={field.hint || ''}
                              rows={2}
                            />
                          )}
                          {field.hint && field.field_type !== 'textarea' && (
                            <p className="goals-section__field-hint">{field.hint}</p>
                          )}
                        </div>
                      ))}
                      {template.fields.length === 0 && (
                        <p className="goals-section__no-fields">No details needed for this goal.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="goals-section__footer">
        {milestone ? (
          <span className={`goals-section__milestone-pill ${completeCount === selectedCount && selectedCount > 0 ? 'goals-section__milestone-pill--done' : ''}`}>
            {milestone.emoji} {milestone.text}
          </span>
        ) : (
          <span />
        )}
        <button className="btn--primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Goals'}
        </button>
      </div>
    </div>
  );
}
